import { MaskedIcon } from '@/components/MaskedIcon/MaskedIcon';
import Plus from '@/images/plus.svg';
import Trash from '@/images/Trash.svg';
import theme from '@/theme';
import { Button } from '@de-id/ui-components';
import styled from '@emotion/styled';
import { useFieldArray, useFormContext } from 'react-hook-form';
import { WebhookFormValues } from '../../../types';
import { FieldLabel } from '../../common';
import { InputField } from '../../common/InputField';

const QueryParamFieldContainer = styled.div`
    display: flex;
    flex-direction: column;
    gap: 8px;
`;

const QueryParamRow = styled.div`
    display: flex;
    gap: 8px;
    align-items: start;
    margin-bottom: 8px;
`;

const StyledAddButton = styled(Button)`
    width: min-content;
    white-space: nowrap;
    & > span {
        gap: 4px;
    }
`;

const StyledValueInputField = styled(InputField)`
    width: 100%;
`;

const StyledTrashButton = styled(Button)`
    margin-top: 24px;
    width: 40px;
    height: 40px;
`;

const InputWithLabelContainer = styled.div`
    display: flex;
    flex-direction: column;
    gap: 4px;
    width: 100%;
`;

const StyledNameInputWithLabelContainer = styled(InputWithLabelContainer)`
    width: 260px;
`;

export const QueryParams = () => {
    const {
        control,
        register,
        formState: { errors },
    } = useFormContext<WebhookFormValues>();

    const { fields, append, remove } = useFieldArray({
        control,
        name: 'queryParams',
    });

    return (
        <QueryParamFieldContainer>
            <FieldLabel
                label="Query parameters"
                optional
                tooltip="Append key–value pairs to the URL (e.g., ?env=prod). We’ll URL-encode values and skip duplicates already present in the URL."
            />
            {fields.map((field, index) => (
                <QueryParamRow key={field.id}>
                    <StyledNameInputWithLabelContainer>
                        <FieldLabel label="Name" />
                        <InputField
                            placeholder="Name"
                            inputProps={{
                                ...register(`queryParams.${index}.name`, { required: 'Enter a query param name' }),
                            }}
                            error={!!errors.queryParams?.[index]?.name}
                            helperText={errors.queryParams?.[index]?.name?.message}
                        />
                    </StyledNameInputWithLabelContainer>
                    <InputWithLabelContainer>
                        <FieldLabel label="Value" />
                        <StyledValueInputField
                            placeholder="Value"
                            inputProps={{
                                ...register(`queryParams.${index}.value`, { required: 'Enter a query param value' }),
                            }}
                            error={!!errors.queryParams?.[index]?.value}
                            helperText={errors.queryParams?.[index]?.value?.message}
                        />
                    </InputWithLabelContainer>
                    <StyledTrashButton size="small" color="outlined" onClick={() => remove(index)}>
                        <MaskedIcon src={Trash} size="16px" />
                    </StyledTrashButton>
                </QueryParamRow>
            ))}
            <StyledAddButton size="small" color="outlined" onClick={() => append({ name: '', value: '' })}>
                <MaskedIcon src={Plus} size="10px" color={theme.colors.secondary.main} />
                Add parameter
            </StyledAddButton>
        </QueryParamFieldContainer>
    );
};
